import React, { useState, useEffect } from "react";
import axios from "axios";
import { Ship, RefreshCw, Search, Users } from "lucide-react";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Passcode for dock access
const DOCK_PASSCODE = "boating";

export default function DockPage() {
  const [authenticated, setAuthenticated] = useState(false);
  const [passcode, setPasscode] = useState("");
  const [error, setError] = useState("");
  const [campers, setCampers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [lastUpdated, setLastUpdated] = useState(null);
  const [secondsUntilRefresh, setSecondsUntilRefresh] = useState(60);

  // Check if already authenticated this session
  useEffect(() => {
    const dockAuth = sessionStorage.getItem("dock_authenticated");
    if (dockAuth === "true") {
      setAuthenticated(true);
      fetchBlueCampers();
    }
  }, []);

  // Auto-refresh every 60 seconds when authenticated
  useEffect(() => {
    if (!authenticated) return;
    
    // Reset countdown when data is fetched
    setSecondsUntilRefresh(60);
    
    // Countdown timer
    const countdownInterval = setInterval(() => {
      setSecondsUntilRefresh(prev => {
        if (prev <= 1) {
          fetchBlueCampers();
          return 60;
        }
        return prev - 1;
      });
    }, 1000);
    
    return () => clearInterval(countdownInterval);
  }, [authenticated]);

  const handlePasscodeSubmit = (e) => {
    e.preventDefault();
    if (passcode.toLowerCase() === DOCK_PASSCODE) {
      sessionStorage.setItem("dock_authenticated", "true");
      setAuthenticated(true);
      setError("");
      fetchBlueCampers();
    } else {
      setError("Incorrect passcode");
    }
  };

  const fetchBlueCampers = async () => {
    setLoading(true);
    try {
      console.log("Fetching campers from:", `${API}/campers`);
      const resp = await axios.get(`${API}/campers`);
      console.log("Got campers:", resp.data?.length || 0, "Response:", resp.status);
      
      // Filter campers with "blue" in their swim color (case insensitive)
      const blueCampers = resp.data.filter(c => {
        const color = (c.swim_color || "").toLowerCase();
        return color.includes("blue");
      });
      
      console.log("Blue campers found:", blueCampers.length);
      
      // Sort by division then by last name
      blueCampers.sort((a, b) => {
        if (a.division !== b.division) return a.division.localeCompare(b.division);
        return a.last_name.localeCompare(b.last_name);
      });
      
      setCampers(blueCampers);
      setLastUpdated(new Date());
    } catch (error) {
      console.error("Error fetching campers:", error);
    }
    setLoading(false);
  };

  // Filter by search query - searches name, group name, and group code
  const filteredCampers = campers.filter(c => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    const fullName = `${c.first_name} ${c.last_name}`.toLowerCase();
    const bunk = (c.bunk || "").toLowerCase();
    const groupCode = (c.group_code || "").toLowerCase();
    return fullName.includes(query) || bunk.includes(query) || groupCode.includes(query);
  });

  // Group by division
  const groupedByDivision = filteredCampers.reduce((acc, camper) => {
    const div = camper.division || "Other";
    if (!acc[div]) acc[div] = [];
    acc[div].push(camper);
    return acc;
  }, {});

  // Color badge component
  const ColorBadge = ({ color }) => {
    const colorLower = (color || "").toLowerCase();
    let bgClass = "bg-blue-500";
    let label = color;
    
    if (colorLower.includes("red") && colorLower.includes("blue")) {
      bgClass = "bg-gradient-to-r from-red-500 to-blue-500";
      label = "R/B";
    } else if (colorLower.includes("yellow") && colorLower.includes("blue")) {
      bgClass = "bg-gradient-to-r from-yellow-400 to-blue-500";
      label = "Y/B";
    } else if (colorLower.includes("green") && colorLower.includes("blue")) {
      bgClass = "bg-gradient-to-r from-green-500 to-blue-500";
      label = "G/B";
    } else {
      label = "Blue";
    }
    
    return (
      <span className={`${bgClass} text-white text-xs font-bold px-2 py-1 rounded-full`}>
        {label}
      </span>
    );
  };

  // Passcode screen
  if (!authenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-blue-900 to-blue-700 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-md">
          <div className="text-center mb-6">
            <Ship className="h-16 w-16 text-blue-600 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-gray-800">Dock Access</h1>
            <p className="text-gray-500 mt-2">Boating Staff Only</p>
          </div>
          
          <form onSubmit={handlePasscodeSubmit}>
            <Input
              type="password"
              placeholder="Enter passcode"
              value={passcode}
              onChange={(e) => setPasscode(e.target.value)}
              className="text-center text-xl tracking-widest mb-4 text-black bg-white border-gray-300"
              autoFocus
            />
            {error && (
              <p className="text-red-500 text-center text-sm mb-4">{error}</p>
            )}
            <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700 text-white">
              Enter
            </Button>
          </form>
        </div>
      </div>
    );
  }

  // Main dock view
  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-900 to-blue-700">
      {/* Header */}
      <div className="bg-blue-800 shadow-lg sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Ship className="h-8 w-8 text-white" />
              <div>
                <h1 className="text-xl font-bold text-white">Dock List</h1>
                <p className="text-blue-200 text-sm">
                  {campers.length} campers with Blue access
                </p>
              </div>
            </div>
            <Button
              onClick={fetchBlueCampers}
              disabled={loading}
              variant="outline"
              className="bg-white/10 border-white/30 text-white hover:bg-white/20"
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </Button>
          </div>
          
          {/* Search */}
          <div className="mt-4 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-blue-300" />
            <Input
              type="text"
              placeholder="Search by name, group name, or group # (e.g. B1)..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 bg-white/10 border-white/30 text-white placeholder:text-blue-300"
            />
          </div>
          
          {lastUpdated && (
            <p className="text-blue-300 text-xs mt-2">
              Last updated: {lastUpdated.toLocaleString('en-US', { timeZone: 'America/New_York' })} • Auto-refresh in {secondsUntilRefresh}s
            </p>
          )}
        </div>
      </div>

      {/* Camper List */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <RefreshCw className="h-8 w-8 text-white animate-spin" />
          </div>
        ) : (
          <div className="space-y-6">
            {Object.entries(groupedByDivision).map(([division, divCampers]) => (
              <div key={division} className="bg-white/10 backdrop-blur-sm rounded-xl p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Users className="h-5 w-5 text-blue-300" />
                  <h2 className="text-lg font-bold text-white">{division}</h2>
                  <span className="text-blue-300 text-sm">({divCampers.length})</span>
                </div>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                  {divCampers.map((camper) => (
                    <div
                      key={camper.id}
                      className="bg-white/10 rounded-lg px-3 py-2 flex items-center justify-between"
                    >
                      <div>
                        <p className="text-white font-medium">
                          {camper.first_name} {camper.last_name}
                        </p>
                        <p className="text-blue-200 text-xs">
                          {camper.group_code && <span className="font-bold">{camper.group_code}</span>}
                          {camper.group_code && camper.bunk && " - "}
                          {camper.bunk}
                        </p>
                      </div>
                      <ColorBadge color={camper.swim_color} />
                    </div>
                  ))}
                </div>
              </div>
            ))}
            
            {filteredCampers.length === 0 && (
              <div className="text-center py-20">
                <Ship className="h-16 w-16 text-blue-400 mx-auto mb-4 opacity-50" />
                <p className="text-blue-200 text-lg">No campers found</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
